import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRegisterMutation, useUpdateMutation } from '../../app/services/groups';
import { fetchGroup, fetchGroups } from '../../features/groups/groupsSlice';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { iconButtonStyles } from '../../utils/iconButtonStyles';
import { MODAL_STYLES } from '../../utils/modalStyles';
import DragDropFileUpload from '../DragDropFileUpload';
import ModalComponent from '../ModalComponent';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';

import {
  TextField,
  Typography,
  DialogContent,
  DialogTitle,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  ModalDialogActions,
  ModalButton,
  ModalCloseButton,
  ModalAddButton,
} from '../ModalComponents';
import EditIcon from '@mui/icons-material/Edit';

import styles from '../ModalComponent/modalStyles.module.css';
import { useTranslation } from 'react-i18next';

const initialForm = {
  name: '',
  description: '',
  requires_iq: false,
  requires_oq: false,
  requires_pq: false,
};

export default function ModalAddGroup({ id, type }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [registerGroup, { error }] = useRegisterMutation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageLink, setImageLink] = useState(null);
  const [fileSizeError, setFileSizeError] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [updateGroups] = useUpdateMutation();

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    setSelectedFile(null);
    setImageLink(null);
    setFileSizeError('');
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (id && open) {
      async function fetchData() {
        try {
          const response = await dispatch(fetchGroup(id));
          setForm({
            name: response.payload.name,
            description: response.payload.description,
            requires_iq: !!response.payload.requires_iq,
            requires_oq: !!response.payload.requires_oq,
            requires_pq: !!response.payload.requires_pq,
          });
          setImageLink(response.payload.image);
        } catch (error) {
          console.error('Error fetching group:', error);
        }
      }
      fetchData();
    }
  }, [id, open, dispatch]);

  useEffect(() => {
    if (error) {
      console.log(error);
      setErrorResponse(error.data?.message);
    }
  }, [error]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);

    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (id) {
      try {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description);
        if (selectedFile) {
          formData.append('image', selectedFile);
        }
        formData.append('requires_iq', form.requires_iq ? 'true' : 'false');
        formData.append('requires_oq', form.requires_oq ? 'true' : 'false');
        formData.append('requires_pq', form.requires_pq ? 'true' : 'false');
        await updateGroups({ id, formData });
        doClose();
        dispatch(fetchGroups());
      } catch (err) {
        console.error('Error update Group:', err);
      }
    } else {
      try {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('description', form.description);
        formData.append('status', 'active');
        formData.append('image', selectedFile);
        formData.append('requires_iq', form.requires_iq ? 'true' : 'false');
        formData.append('requires_oq', form.requires_oq ? 'true' : 'false');
        formData.append('requires_pq', form.requires_pq ? 'true' : 'false');
        await registerGroup(formData);
        dispatch(fetchGroups());
      } catch (err) {
        console.error('Error registering group:', err);
      }
    }
  };

  return (
    <div>
      {type === 'add' ? (
        <ModalAddButton onClick={handleOpen} />
      ) : (
        <Tooltip title={t('generic.edit')} placement="top">
          <IconButton color="primary" onClick={handleOpen} style={iconButtonStyles}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={MODAL_STYLES.dialogTitle}>
            {type === 'add' ? t('modals.createNewGroup') : t('modals.updateGroup')}
          </DialogTitle>
          <ModalCloseButton onClick={handleClose} />

          <DialogContent dividers sx={MODAL_STYLES.dialogContentWithScroll}>
            <div style={MODAL_STYLES.formContainer}>
              <TextField
                value={form.name}
                label={t('generic.name')}
                variant="outlined"
                name="name"
                onChange={handleChange}
                fullWidth
                sx={commonTextFieldStyles}
              />
              <TextField
                value={form.description}
                label={t('generic.description')}
                variant="outlined"
                name="description"
                onChange={handleChange}
                fullWidth
                sx={commonTextFieldStyles}
              />

              <FormGroup row sx={{ gap: 2, mt: 1, mb: 1 }}>
                {['iq', 'oq', 'pq'].map((kind) => {
                  const field = `requires_${kind}`;
                  return (
                    <FormControlLabel
                      key={kind}
                      control={
                        <Checkbox
                          checked={!!form[field]}
                          onChange={(e) => {
                            setIsDirty(true);
                            setForm({ ...form, [field]: e.target.checked });
                          }}
                        />
                      }
                      label={t(`validation.requires${kind.toUpperCase()}`)}
                    />
                  );
                })}
              </FormGroup>

              <DragDropFileUpload
                accept="image/*"
                selectedFile={selectedFile}
                onFileChange={(file, error) => {
                  setIsDirty(true);
                  if (error) {
                    setFileSizeError(error);
                    setSelectedFile(null);
                  } else {
                    setFileSizeError('');
                    setSelectedFile(file);
                  }
                }}
                onFileRemove={() => {
                  setIsDirty(true);
                  setSelectedFile(null);
                  setFileSizeError('');
                }}
                error={fileSizeError}
                maxSize={process.env.REACT_APP_MAX_GROUP_IMAGE_FILE_SIZE}
                placeholder={type === 'add' ? t('modals.uploadImage') : t('modals.updateImage')}
              />

              {id && !selectedFile && imageLink && (
                <img
                  src={`${process.env.REACT_APP_BASE_URL}machine-groups/image/${imageLink}`}
                  alt="Uploaded file"
                  style={{
                    maxWidth: '100%',
                    marginTop: '10px',
                    borderRadius: '8px',
                    border: '1px solid var(--table-border)',
                  }}
                />
              )}
              {errorResponse &&
                (Array.isArray(errorResponse) ? (
                  errorResponse.map((item, index) => (
                    <Typography key={index} color="error">
                      {item}
                    </Typography>
                  ))
                ) : (
                  <Typography color="error">{errorResponse}</Typography>
                ))}
            </div>
          </DialogContent>

          <ModalDialogActions>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.cancel')}
            </ModalButton>
            <ModalButton variant="contained" color="primary" onClick={handleSubmit}>
              {type === 'add' ? t('modals.create') : t('modals.update')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
